import hash from 'object-hash'
import { computed, ref, type Ref } from 'vue'

/**
 * Tracks whether a reactive ref has changed since the last snapshot.
 * Handles both API model objects (with toJSON()) and plain objects,
 * stripping Vue reactive proxies before hashing to avoid false positives.
 *
 * Usage:
 *   const item = ref<any>(null)
 *   const { unsavedChanges, snapshot } = useUnsavedChanges(item)
 *
 *   // After fetch/save:
 *   item.value = response.data
 *   snapshot()
 */
export function useUnsavedChanges<T>(data: Ref<T | null>) {
  const originalHash = ref('')

  function serialize(value: unknown): unknown {
    if (value && typeof (value as any).toJSON === 'function') {
      return (value as any).toJSON()
    }
    return JSON.parse(JSON.stringify(value))
  }

  const unsavedChanges = computed(() => {
    if (!originalHash.value || !data.value) return false
    return hash(serialize(data.value)) !== originalHash.value
  })

  function snapshot() {
    if (data.value) {
      originalHash.value = hash(serialize(data.value))
    }
  }

  function reset() {
    originalHash.value = ''
  }

  return { unsavedChanges, snapshot, reset }
}
